
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"
import _ from "lodash"

import { CaseDetails, CaseField } from "@/types"

import { TimeSpentAsValue, TimeSpentTotal, TimeSpentEntry } from "@/types/timeSpentAsValue"
import { caseColumns } from "@/types/caseColumns"
import { TenantFeatures } from "@/types/tenantFeatures"

enum TrackAfterClosureSetting {
	Nothing,
	Changes,
	Time,
	Both
}

@Component({
})
export default class TimeSpentDisplay extends Vue {
	@Prop()
	public caseField!: CaseField

	@Prop()
	public timeSpentAsValue!: TimeSpentAsValue;

	@Prop()
	public currentCase!: CaseDetails;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter getNomenclatureForRecord!: (record: CaseDetails) => string

	private TenantFeatures = TenantFeatures
	private TrackAfterClosureSetting = TrackAfterClosureSetting

	public get trackingFieldId (): string {
		const extensions = this.caseField.field.extensions ? this.caseField.field.extensions : (this.caseField.field as any).fieldExtensions
		if (!extensions) {
			throw Error("No field extension")
		}
		return extensions.trackingFieldId
	}

	public get trackingCaseFieldName (): string {
		// if a user defined field
		if (this.currentCase.fields[this.trackingFieldId]) {
			return this.currentCase.fields[this.trackingFieldId].field.title
		}

		// else a built in/global field
		const builtInField = caseColumns.find(c => c.key === this.trackingFieldId)
		if (builtInField) {
			return builtInField.label
		}

		return "Error: Could not find tracking field"
	}

	public get lastValueSet (): string {
		const lastValue = _.chain(this.timeSpentAsValue.entries)
			.orderBy("started")
			.last()
			.value()
			.fieldValue

		return lastValue || "No Value Set"
	}

	/**
	 * The current tracking setting for time and changes after case closure.
	 */
	private get trackAfterClosureSetting (): TrackAfterClosureSetting {
		const trackTimeAfterCaseClosure = this.getIsFeatureEnabled(TenantFeatures.TrackTimeSpentFieldsTimeAfterCaseClosure)
		const trackChangesAfterCaseClosure = this.getIsFeatureEnabled(TenantFeatures.TrackTimeSpentFieldsChangesAfterCaseClosure)

		if (trackTimeAfterCaseClosure && trackChangesAfterCaseClosure) {
			return TrackAfterClosureSetting.Both
		} else if (trackTimeAfterCaseClosure) {
			return TrackAfterClosureSetting.Time
		} else if (trackChangesAfterCaseClosure) {
			return TrackAfterClosureSetting.Changes
		} else {
			return TrackAfterClosureSetting.Nothing
		}
	}

	/**
	 * A description of what is being tracked after case closure.
	 */
	private get trackAfterClosureDescription (): string {
		switch (this.trackAfterClosureSetting) {
			case TrackAfterClosureSetting.Nothing:
				return "Time and changes are no longer being tracked."
			case TrackAfterClosureSetting.Changes:
				return "Time is no longer being tracked, but changes are."
			case TrackAfterClosureSetting.Time:
				return "Changes are no longer being tracked, but time is."
			case TrackAfterClosureSetting.Both:
				return "Time and changes are still being tracked."
		}
	}
}
